// frontend/src/pages/VerificationRequest.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL;

type VenueLite = { _id: string; name: string };

type RequestBody = {
  category: 'professional' | 'venue';
  venueId?: string;
  desiredTier?: 'professional' | 'certified_professional' | 'verified_venue' | 'certified_venue';
  message?: string;
};

export default function VerificationRequest({ token, onNavigate }: any) {
  const [category, setCategory] = useState<'professional'|'venue'>('professional');
  const [desiredTier, setDesiredTier] = useState<string>('professional');
  const [venueId, setVenueId] = useState<string>('');
  const [venues, setVenues] = useState<VenueLite[]>([]);
  const [message, setMessage] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState<any[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const v = await axios.get(`${API}/venues/my`, { headers });
        setVenues((v.data?.venues || []).map((x: any) => ({ _id: x._id, name: x.name })));
        const r = await axios.get(`${API}/verifications/requests/mine`, { headers });
        setMine(r.data?.requests || []);
      } catch {}
    }
    load();
  }, [token]);

  async function submit() {
    if (!token) return alert('Please log in');
    try {
      setSubmitting(true);
      const body: RequestBody = { category, message } as any;
      if (category === 'venue') {
        if (!venueId) { alert('Please select a venue'); setSubmitting(false); return; }
        body.venueId = venueId;
        body.desiredTier = desiredTier === 'certified_venue' ? 'certified_venue' : 'verified_venue';
      } else {
        body.desiredTier = desiredTier === 'certified_professional' ? 'certified_professional' : 'professional';
      }
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/verifications/requests`, body, { headers });
      alert('Request submitted');
      const r = await axios.get(`${API}/verifications/requests/mine`, { headers });
      setMine(r.data?.requests || []);
      setMessage('');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen themed-page p-3 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Request Verification</h2>
          <p className="mt-1 text-[13px] sm:text-sm text-theme-secondary">Submit your verification request. Admin reviews typically take 24–48 hours.</p>
        </div>

        <div className="p-4 sm:p-5 rounded-xl themed-card space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1">
              <label className="text-xs sm:text-sm text-theme-secondary">Category</label>
              <select className="input w-full px-3 py-2 text-sm rounded-md" value={category} onChange={(e) => setCategory(e.target.value as any)}>
                <option value="professional">Professional</option>
                <option value="venue">Venue</option>
              </select>
            </div>

            {category === 'venue' ? (
              <>
                <div className="space-y-1">
                  <label className="text-xs sm:text-sm text-theme-secondary">Venue</label>
                  <select className="input w-full px-3 py-2 text-sm rounded-md" value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                    <option value="">Select a venue…</option>
                    {venues.map((v) => (
                      <option key={v._id} value={v._id}>{v.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] sm:text-xs text-theme-secondary">Choose from your created venues.</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs sm:text-sm text-theme-secondary">Requested Tier</label>
                  <select className="input w-full px-3 py-2 text-sm rounded-md" value={desiredTier} onChange={(e) => setDesiredTier(e.target.value)}>
                    <option value="verified_venue">Verified Venue</option>
                    <option value="certified_venue">Certified Venue</option>
                  </select>
                  <p className="text-[11px] sm:text-xs text-theme-secondary">Certified requires additional proof and may take longer.</p>
                </div>
              </>
            ) : (
              <div className="space-y-1">
                <label className="text-xs sm:text-sm text-theme-secondary">Requested Tier</label>
                <select className="input w-full px-3 py-2 text-sm rounded-md" value={desiredTier} onChange={(e) => setDesiredTier(e.target.value)}>
                  <option value="professional">Professional</option>
                  <option value="certified_professional">Certified Professional</option>
                </select>
                <p className="text-[11px] sm:text-xs text-theme-secondary">Provide details in the notes to speed up review.</p>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs sm:text-sm text-theme-secondary">Notes (optional)</label>
            <textarea className="input w-full px-3 py-2 text-sm rounded-md" rows={4} placeholder="Share qualifications, links, or documents" value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button className="btn w-full sm:w-auto" onClick={submit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit Request'}</button>
            {onNavigate && (
              <button className="px-3 py-2 text-xs rounded-md border w-full sm:w-auto" style={{ borderColor: 'var(--border)' }} onClick={() => onNavigate('dashboard')}>Back</button>
            )}
          </div>
        </div>

        {/* My requests */}
        <section className="p-4 sm:p-5 rounded-xl themed-card">
          <h3 className="text-sm font-semibold text-heading mb-3">My Requests</h3>
          {mine.length === 0 ? (
            <div className="text-xs text-theme-secondary">No requests yet.</div>
          ) : (
            <ul className="space-y-2">
              {mine.map((r: any) => (
                <li key={r._id} className="p-3 rounded-lg border text-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex-1">
                    <span className="font-semibold capitalize">{r.category}</span>
                    {r.venue ? <span className="ml-1">• {String(r.venue)}</span> : null}
                    {r.grantTier ? <span className="ml-1">• {r.grantTier}</span> : null}
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] border" style={{ borderColor: 'var(--border)' }}>{r.status}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
